import React, {useEffect} from 'react';
import {makeStyles, useTheme} from '@material-ui/core/styles';
import CssBaseline from '@material-ui/core/CssBaseline';
import {Header} from "../layout/header/Header";
import {SideBar} from "../layout/sideBar/SideBar";
import {MainContainer} from '../layout/MainContainer';
import {useCapability} from "../auth/CapabilityContext";
import {useTranslation} from "react-i18next";
import {MuiPickersUtilsProvider} from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";
import frLocale from "date-fns/locale/fr";
import enLocale from "date-fns/locale/en-US";
import {Grid, Typography, useMediaQuery} from "@material-ui/core";
import {Footer} from "../layout/Footer";
import {Roles} from "../auth/roles";
import {run} from "../utils/function-util";
import {getOrganizations} from "../api/organization-functions";
import {useApiNotification} from "../components/ui/ApiNotificationProvider";

const useStyles = makeStyles(theme => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        justifyContent: 'space-between'
    },
    parent: {
        height: "inherit"
    },
    flexContainer: {
        display: 'flex',
        minHeight: 'calc(100vh - 80px)',
        width: '100%'
    }
}));

const localeMap: { [k: string]: Locale } = {
    en: enLocale,
    fr: frLocale
}

export default function Dashboard({...props}) {
    const classes = useStyles();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'), {noSsr: true});
    const [drawerIsOpen, setOpen] = React.useState(!isMobile);
    const [hasRequiredAccess, setHasRequiredAccess] = React.useState(false);
    const {i18n,t} = useTranslation();
    const children = props.children;
    const {capabilities, hasRole, setCapabilities, userRoles} = useCapability();
    useEffect(() => setOpen(true), [isMobile]);
    const {handleError} = useApiNotification();
    const toggleDrawer = () => {
        setOpen(state => !state);
    };
    const handleCloseDrawer = () => {
        setOpen(false);
    };

    let sideBarProps = {
        drawerIsOpen: drawerIsOpen,
        closeDrawerClicked: handleCloseDrawer,
        toggleDrawerClicked: toggleDrawer
    };

    let sideHeaderProps = {
        drawerIsOpen: drawerIsOpen,
        toggleDrawerClicked: toggleDrawer,
    };

    useEffect(run(
        async () => await getOrganizations(),
        (allOrganizations: any) => {
            initOrganizations(allOrganizations);
        },
        handleError
    ), []);

    useEffect(() => {
        if (userRoles && hasRole(Roles.ADMIN)) {
            setHasRequiredAccess(true);
        }
    }, [userRoles, hasRole]);

    const initOrganizations = (organizationsToInit: any) => {
        const initCapabilities = organizationsToInit.reduce((acc: any, curr: any) => ({
            ...acc,
            [curr.id]: capabilities[curr.id] ?? {}
        }), {});
        setCapabilities(initCapabilities);
        setHasRequiredAccess(hasRole(Roles.ADMIN) || Object.values(initCapabilities).some(c => c !== null && c !== undefined));
    }

    return (
        <MuiPickersUtilsProvider utils={DateFnsUtils} locale={localeMap[i18n.language]}>
            <Grid container className={classes.root}>
                <CssBaseline/>
                    <Header {...sideHeaderProps} />
                    {hasRequiredAccess ?
                    <Grid item className={classes.flexContainer}>
                        <SideBar {...sideBarProps} />
                        {!(isMobile && drawerIsOpen) &&
                        <MainContainer>
                            {children || ''}
                        </MainContainer>
                        }
                    </Grid> :
                    <Grid item className={classes.flexContainer}>
                        <MainContainer>
                            <Typography variant="subtitle1" align="center">{t('_Common.Api.Messages.RequireToBeAdministratorToAccessDashboard')}</Typography>
                        </MainContainer>
                    </Grid>
                    }
                    <Footer/>
            </Grid>
        </MuiPickersUtilsProvider>
    );
}
